import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { ArrowLeft, Calendar, User } from 'lucide-react';
import { blogPosts } from '../components/blog/blogData';
import BlogAuthor from '../components/blog/BlogAuthor';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import GlossaryAutoLinker from '../components/glossary/GlossaryAutoLinker';
import PatternSignalExplainer from '../components/blog/PatternSignalExplainer';
import PatternSignalWorkedExample from '../components/blog/PatternSignalWorkedExample';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find(post => post.slug === slug);
  
  if (!post) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-24 pb-12">
          <div className="section-inner">
            <div className="text-center py-16">
              <h1 className="text-3xl font-bold mb-4">Post Not Found</h1>
              <p className="mb-6">The blog post you're looking for doesn't exist.</p>
              <Link to="/blog" className="text-brand-teal hover:underline inline-flex items-center">
                <ArrowLeft size={16} className="mr-2" /> Back to Blog
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // Helper function to process markdown formatting
  const processMarkdown = (text: string): string => {
    if (!text) return '';
    let processed = text;
    
    // Handle markdown links [text](url)
    processed = processed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, 
      '<a href="$2" class="text-brand-teal hover:underline font-semibold">$1</a>');
    
    // Handle bold text **text** and __text__
    processed = processed
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>');
    
    // Handle specific text replacements for contact links (fallback)
    processed = processed
      .replace(/reach out to us(?!\<)/g, '<a href="/contact" class="text-brand-teal hover:underline">reach out to us</a>')
      .replace(/Contact ThinkTomorrow\.AI(?!\<)/g, '<a href="/contact" class="text-brand-teal hover:underline font-semibold">Contact ThinkTomorrow.AI</a>')
      .replace(/Think Tomorrow AI(?!\<)/g, '<a href="/contact" class="text-brand-teal hover:underline font-semibold">Think Tomorrow AI</a>');
    
    return processed;
  };

  const renderContent = (content: string[]) => {
    const result: React.ReactNode[] = [];
    let tableRows: string[][] = [];
    let isInTable = false;
    let tableStartIndex = 0;

    const flushTable = () => {
      if (tableRows.length > 0) {
        const headerRow = tableRows[0];
        const bodyRows = tableRows.slice(1);
        
        result.push(
          <div key={`table-${tableStartIndex}`} className="overflow-x-auto my-6">
            <table className="w-full border-collapse border border-border rounded-lg">
              <thead className="bg-muted">
                <tr>
                  {headerRow.map((cell, cellIdx) => (
                    <th key={cellIdx} className="border border-border px-4 py-3 text-left font-semibold text-foreground">
                      {cell}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((row, rowIdx) => (
                  <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-background' : 'bg-muted/50'}>
                    {row.map((cell, cellIdx) => (
                      <td key={cellIdx} className="border border-border px-4 py-3 text-muted-foreground">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        tableRows = [];
        isInTable = false;
      }
    };

    content.forEach((paragraph, index) => {
      if (!paragraph && paragraph !== '') return;

      // Handle custom component markers
      if (paragraph.trim() === '{{COMPONENT:PatternSignalExplainer}}') {
        result.push(<PatternSignalExplainer key={`component-${index}`} />);
        return;
      }
      if (paragraph.trim() === '{{COMPONENT:PatternSignalWorkedExample}}') {
        result.push(<PatternSignalWorkedExample key={`component-${index}`} />);
        return;
      }

      // Detect table rows (lines starting with |)
      if (paragraph.trim().startsWith('|') && paragraph.trim().endsWith('|')) {
        // Skip separator rows (|---|---|---|)
        if (paragraph.includes('---')) {
          return;
        }
        
        if (!isInTable) {
          isInTable = true;
          tableStartIndex = index;
        }
        
        // Parse table cells
        const cells = paragraph
          .split('|')
          .slice(1, -1) // Remove empty first and last elements
          .map(cell => cell.trim());
        
        tableRows.push(cells);
        return;
      }
      
      // If we were in a table and hit a non-table line, flush the table
      if (isInTable) {
        flushTable();
      }

      // Handle Markdown ## headers (h2)
      if (paragraph.trim().startsWith('## ')) {
        const headerText = paragraph.trim().replace(/^##\s+/, '');
        result.push(<h2 key={index} className="text-2xl font-bold mt-8 mb-4">{headerText}</h2>);
        return;
      }
      
      // Handle Markdown ### headers (h3)
      if (paragraph.trim().startsWith('### ')) {
        const headerText = paragraph.trim().replace(/^###\s+/, '');
        result.push(<h3 key={index} className="text-xl font-bold mt-6 mb-3">{headerText}</h3>);
        return;
      }
      
      // Handle standalone bold text as subheadings
      if (paragraph.trim().match(/^\*\*[^*]+\*\*$/)) {
        const headerText = paragraph.trim().replace(/^\*\*|\*\*$/g, '');
        result.push(<h4 key={index} className="font-bold mt-6 mb-2">{headerText}</h4>);
        return;
      }
      
      // Skip bullet points from header detection
      const isBulletPoint = paragraph.trim().startsWith('•') || paragraph.trim().startsWith('-') || paragraph.trim().startsWith('*');
      
      if (!isBulletPoint && (paragraph.trim().startsWith('Real Benefits') ||
          paragraph.trim().startsWith('Practical AI') || 
          paragraph.trim().startsWith('Why SMEs') || 
          paragraph.trim().startsWith('Local Expertise') || 
          paragraph.trim().startsWith('Ready to Explore') ||
          paragraph.trim().startsWith('Step ') ||
          paragraph.trim().startsWith('Case Example') ||
          /^[A-Z][a-z]+ [A-Z][a-z]+:/.test(paragraph) || 
          (paragraph.length < 60 && !paragraph.endsWith('.')))) {
        
        result.push(<h2 key={index} className="text-2xl font-bold mt-8 mb-4">{paragraph}</h2>);
        return;
      }
      else if (paragraph.trim().startsWith('Finance') || 
               paragraph.trim().startsWith('Legal') || 
               paragraph.trim().startsWith('Food') ||
               paragraph.trim().startsWith('Appointment') || 
               paragraph.trim().startsWith('Customer') || 
               paragraph.trim().startsWith('Data Entry') || 
               paragraph.trim().startsWith('Inventory') ||
               paragraph.trim().startsWith('Multimodal') ||
               paragraph.trim().startsWith('Federated') ||
               paragraph.trim().startsWith('Edge AI') ||
               paragraph.trim().startsWith('AI-augmented') ||
               paragraph.trim().startsWith('Explainable') ||
               paragraph.trim().startsWith('Industry-specific') ||
               paragraph.trim().startsWith('Autonomous')) {
        
        result.push(<h3 key={index} className="text-xl font-bold mt-6 mb-3">{paragraph}</h3>);
        return;
      }
      else if (/^\d+\./.test(paragraph.trim())) {
        if (paragraph.includes(' - ')) {
          const parts = paragraph.split(' - ');
          const processedDescription = processMarkdown(parts.slice(1).join(' - '));
          result.push(
            <div key={index} className="mb-4 pl-5">
              <p className="font-semibold">{parts[0]}</p>
              <p dangerouslySetInnerHTML={{ __html: processedDescription }} />
            </div>
          );
          return;
        }
        const processedText = processMarkdown(paragraph.split('.').slice(1).join('.').trim());
        result.push(
          <div key={index} className="mb-4 pl-5 flex">
            <span className="font-semibold mr-2">{paragraph.split('.')[0]}.</span>
            <span dangerouslySetInnerHTML={{ __html: processedText }} />
          </div>
        );
        return;
      }
      else if (paragraph.trim().startsWith('•') || paragraph.trim().startsWith('-') || paragraph.trim().startsWith('*')) {
        const content = paragraph.trim().substring(1).trim();
        const processedContent = processMarkdown(content);
        result.push(
          <p key={index} className="mb-2 ml-5 flex">
            <span className="mr-2">•</span>
            <span dangerouslySetInnerHTML={{ __html: processedContent }} />
          </p>
        );
        return;
      }
      else if (paragraph.includes('Example:') || paragraph.trim().startsWith('Example')) {
        result.push(<p key={index} className="mb-6 font-semibold italic">{paragraph}</p>);
        return;
      }
      else {
        const processedText = processMarkdown(paragraph);
        result.push(<p key={index} className="mb-6" dangerouslySetInnerHTML={{ __html: processedText }} />);
        return;
      }
    });

    // Flush any remaining table at the end
    flushTable();

    return result;
  };
  
  // Function to get the author's avatar
  const getAuthorAvatar = (authorName: string) => {
    if (authorName.includes('&') || authorName.includes('and')) {
      return "/lovable-uploads/1972d93f-9ded-476a-8ebe-a3bae9daa6d1.png";
    } else if (authorName.toLowerCase().includes('darin')) {
      return "/lovable-uploads/dfc24d1a-b270-420d-8318-7135be5f9906.png";
    } else if (authorName.toLowerCase().includes('lucas')) {
      return "/lovable-uploads/5df08f40-9826-454a-8f1d-7c027a288f9d.png";
    }
    return post.author.avatar || null;
  };
  
  // Get absolute URL for the cover image
  const getAbsoluteImageUrl = (imagePath: string | undefined) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    // For imported images (from src/assets), they'll be bundled paths
    // For public folder images, construct the full URL
    const baseUrl = 'https://thinktomorrow.ai';
    if (imagePath.startsWith('/')) return `${baseUrl}${imagePath}`;
    return imagePath;
  };

  const canonicalUrl = `https://thinktomorrow.ai/blog/${post.slug}`;
  const ogImage = getAbsoluteImageUrl(post.coverImage);
  
  return (
    <>
      <Helmet>
        <title>{post.title} | ThinkTomorrow.AI Blog</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph tags */}
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="ThinkTomorrow.AI" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author.name} />
        <meta property="article:section" content={post.category} />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt,
          "image": ogImage,
          "datePublished": post.date,
          "author": { "@type": "Person", "name": post.author.name },
          "publisher": {
            "@type": "Organization",
            "name": "ThinkTomorrow.AI",
            "logo": { "@type": "ImageObject", "url": "https://thinktomorrow.ai/lovable-uploads/46a89bb9-6390-44d4-8240-2fc3f393f3ba.png" }
          },
          "mainEntityOfPage": canonicalUrl
        })}</script>
      </Helmet>
      
      <Navbar />
      <main className="min-h-screen pt-16 md:pt-24">
        <div className="bg-brand-navy/5 py-12">
          <div className="section-inner max-w-4xl mx-auto">
            <Link to="/blog" className="text-brand-teal hover:underline inline-flex items-center mb-4">
              <ArrowLeft size={16} className="mr-2" /> Back to Blog
            </Link>
            
            <h1 className="text-3xl md:text-4xl font-bold font-space mb-4">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center text-sm text-gray-600 mb-4 gap-4">
              <span className="flex items-center">
                <Calendar size={16} className="mr-1" />
                {post.date}
              </span>
              <BlogAuthor name={post.author.name} avatar={post.author.avatar} />
              <span className="bg-brand-teal/90 text-white text-xs font-medium py-1 px-2 rounded">
                {post.category}
              </span>
            </div>
          </div>
        </div>
        
        <div className="section-inner max-w-4xl mx-auto py-8">
          <div className="aspect-video rounded-lg overflow-hidden mb-8">
            <img
              src={post.coverImage}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
          
          <article className="prose prose-lg max-w-none">
            <p className="font-bold text-xl mb-6">{post.excerpt}</p>
            <GlossaryAutoLinker maxLinks={5}>
              <div>{renderContent(post.content)}</div>
            </GlossaryAutoLinker>
          </article>
          
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center">
              <Avatar className="h-12 w-12 mr-4 border border-gray-200">
                {post.author.name ? (
                  <AvatarImage 
                    src={getAuthorAvatar(post.author.name)} 
                    alt={post.author.name} 
                    className="object-cover"
                  />
                ) : (
                  <AvatarFallback>
                    <User size={20} />
                  </AvatarFallback>
                )}
              </Avatar>
              <div>
                <h3 className="font-bold text-lg">{post.author.name}</h3>
                <p className="text-gray-600">{post.author.bio}</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default BlogPost;
